// components/SelectImage.js - Modified and Enhanced Version
import React, { useCallback, useState, useRef } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Text,
  Box,
  IconButton,
  VStack,
  useToast,
  Tooltip,
  Badge,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { FiUpload, FiX, FiEye, FiFile } from "react-icons/fi";

const SelectImage = ({
  onChange = () => {},
  label = "Select File",
  value = null, // Changed from url to value for better consistency
  filename,
  mt = 2,
  error,
  errorBorder,
  doctype = "image/png, image/jpg, image/jpeg, .pdf",
  multiple = false,
  maxSize = 5 * 1024 * 1024, // 5MB default
  required = false,
  disabled = false,
  previewHeight = "200px",
  previewWidth = "xs",
  showPreview = true,
  placeholder = "Choose file",
  ...rest
}) => {
  const inputRef = useRef(null);
  const toast = useToast();
  const [isDragOver, setIsDragOver] = useState(false);

  // Get file extension
  const getFileExtension = useCallback((file) => {
    if (!file) return "";
    const fileName = file.name || file;
    return fileName.split(/[#?]/)[0]?.split(".")?.pop()?.trim().toLowerCase();
  }, []);

  // Check if file is image
  const isImage = useCallback(
    (file) => {
      if (!file) return false;
      const extension = getFileExtension(file);
      return ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(extension);
    },
    [getFileExtension]
  );

  // Check if file is PDF
  const isPDF = useCallback(
    (file) => {
      if (!file) return false;
      const extension = getFileExtension(file);
      return extension === "pdf";
    },
    [getFileExtension]
  );

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  // Validate file
  const validateFile = useCallback(
    (file) => {
      if (!file) return null;

      // Check file size
      if (file.size > maxSize) {
        return `File size too large. Maximum size allowed: ${formatFileSize(
          maxSize
        )}`;
      }

      // Check file type if doctype is specified
      if (doctype) {
        const allowedTypes = doctype
          .split(",")
          .map((type) => type.trim().toLowerCase());
        const fileExtension = `.${getFileExtension(file)}`;
        const mimeType = file.type.toLowerCase();

        const isAllowed = allowedTypes.some((type) =>
          type.startsWith(".") ? type === fileExtension : type === mimeType
        );

        if (!isAllowed) {
          return `File type not allowed. Accepted types: ${doctype}`;
        }
      }

      return null;
    },
    [maxSize, doctype, formatFileSize, getFileExtension]
  );

  // Handle file selection
  const handleFileChange = useCallback(
    (event) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0]; // Take first file for single selection
      const validationError = validateFile(file);

      if (validationError) {
        toast({
          title: "Invalid File",
          description: validationError,
          status: "error",
          duration: 4000,
          isClosable: true,
        });

        // Reset input
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }

      // Call onChange with file object
      onChange(file);

      toast({
        title: "File Selected",
        description: `${file.name} (${formatFileSize(file.size)})`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // Reset input value to allow selecting same file again
      event.target.value = "";
    },
    [onChange, validateFile, toast, formatFileSize]
  );

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        const validationError = validateFile(file);

        if (validationError) {
          toast({
            title: "Invalid File",
            description: validationError,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
          return;
        }

        onChange(file);

        toast({
          title: "File Selected",
          description: `${file.name} (${formatFileSize(file.size)})`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    },
    [onChange, validateFile, toast, formatFileSize]
  );

  // Open file dialog
  const openFileDialog = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  // Remove selected file
  const removeFile = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [onChange]
  );

  // Get preview URL
  const getPreviewURL = useCallback(() => {
    if (!value) return null;

    if (value.name) {
      // File object
      return URL.createObjectURL(value);
    } else if (typeof value === "string") {
      // URL string
      return value;
    }

    return null;
  }, [value]);

  // Get display filename
  const getDisplayFilename = useCallback(() => {
    if (filename) return filename;
    if (value?.name) return value.name;
    if (typeof value === "string") return value.split("/").pop();
    return placeholder;
  }, [filename, value, placeholder]);

  const previewURL = getPreviewURL();
  const displayFilename = getDisplayFilename();
  const hasFile = !!value;

  return (
    <FormControl flex={1} mt={mt} isInvalid={!!error} {...rest}>
      <FormLabel
        marginBottom={2}
        marginLeft={2}
        fontSize="sm"
        fontWeight="600"
        color="gray.700"
      >
        {label}
        {required && (
          <Text as="span" color="red.500" ml={1}>
            *
          </Text>
        )}
      </FormLabel>

      {/* File Preview */}
      {hasFile && showPreview && (
        <Box position="relative" mb={3}>
          {isImage(value) && previewURL ? (
            <Box position="relative" display="inline-block">
              <Image
                src={previewURL}
                h={previewHeight}
                w={previewWidth}
                borderRadius={8}
                objectFit="cover"
                border="2px solid"
                borderColor="gray.200"
                _hover={{ borderColor: "gray.300" }}
              />
              <IconButton
                icon={<FiX />}
                size="sm"
                colorScheme="red"
                variant="solid"
                position="absolute"
                top={2}
                right={2}
                onClick={removeFile}
                aria-label="Remove file"
                zIndex={2}
              />
            </Box>
          ) : isPDF(value) ? (
            <VStack
              spacing={2}
              p={4}
              border="2px solid"
              borderColor="gray.200"
              borderRadius={8}
              bg="gray.50"
              maxW={previewWidth}
            >
              <Icon as={FiFile} boxSize={8} color="red.500" />
              <Text fontSize="sm" fontWeight="medium" textAlign="center">
                PDF Document
              </Text>
              <Text
                fontSize="xs"
                color="gray.600"
                textAlign="center"
                noOfLines={1}
              >
                {displayFilename}
              </Text>
              {value?.size && (
                <Badge colorScheme="gray" fontSize="xs">
                  {formatFileSize(value.size)}
                </Badge>
              )}
              <IconButton
                icon={<FiX />}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={removeFile}
                aria-label="Remove file"
              />
            </VStack>
          ) : null}
        </Box>
      )}

      {/* File Input Area */}
      <Box
        border="2px solid"
        borderColor={error ? "red.300" : isDragOver ? "blue.300" : "#EBEAEA"}
        borderRadius={5}
        bg={isDragOver ? "blue.50" : "white"}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
        _hover={
          !disabled
            ? {
                borderColor: error
                  ? "red.400"
                  : isDragOver
                  ? "blue.400"
                  : "gray.300",
                bg: isDragOver ? "blue.100" : "gray.50",
              }
            : {}
        }
        transition="all 0.2s"
      >
        <HStack spacing={0} h={10} alignItems="center" overflow="hidden">
          <Flex
            bg="#EBEAEA"
            h={10}
            px={4}
            alignItems="center"
            justifyContent="center"
            borderRightRadius={0}
            borderRadius={5}
          >
            <Icon as={FiUpload} mr={2} />
            <Text fontSize="md" fontWeight="medium">
              Browse
            </Text>
          </Flex>

          <Box flex={1} px={3}>
            <Text
              fontSize="sm"
              color={hasFile ? "gray.700" : "gray.500"}
              noOfLines={1}
            >
              {isDragOver ? "Drop file here..." : displayFilename}
            </Text>
          </Box>

          {hasFile && (
            <Tooltip label="Remove file">
              <IconButton
                icon={<FiX />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                mr={2}
                onClick={removeFile}
                aria-label="Remove file"
              />
            </Tooltip>
          )}
        </HStack>

        <Input
          ref={inputRef}
          type="file"
          onChange={handleFileChange}
          display="none"
          accept={doctype}
          multiple={multiple}
          disabled={disabled}
        />
      </Box>

      {/* File Info */}
      {hasFile && value?.size && (
        <Text fontSize="xs" color="gray.500" mt={1} ml={2}>
          Size: {formatFileSize(value.size)}
        </Text>
      )}

      {/* Error Message */}
      {error && (
        <Text fontSize="sm" color="red.500" mt={1} ml={2}>
          {error}
        </Text>
      )}

      {/* Help Text */}
      {!error && doctype && (
        <Text fontSize="xs" color="gray.400" mt={1} ml={2}>
          Accepted formats: {doctype.replace(/,/g, ", ")}
          {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
        </Text>
      )}
    </FormControl>
  );
};

export default React.memo(SelectImage);
// import {
//   FormControl,
//   FormLabel,
//   HStack,
//   Image,
//   Input,
//   Text,
// } from "@chakra-ui/react";
// import React from "react";

// const SelectImage = ({
//   onChange,
//   label,
//   url,
//   filename,
//   mt,
//   error,
//   errorBorder,
//   doctype,
// }) => {
//   const inputOpenFileRef = React.createRef();
//   const showOpenFileDlg = () => {
//     inputOpenFileRef.current.click();
//   };

//   return (
//     <FormControl flex={1} mt={mt || 0}>
//       <FormLabel marginBottom={2} marginLeft={2}>
//         {label}
//       </FormLabel>
//       {url &&
//         (url?.name?.split(/[#?]/)[0]?.split(".")?.pop().trim() == "pdf" || (
//           <Image
//             src={url.name ? URL.createObjectURL(url) : url}
//             h={"200px"}
//             w={"xs"}
//             borderRadius={8}
//             objectFit={"cover"}
//             overflow={"hidden"}
//             mb={2}
//           />
//         ))}
//       <HStack
//         border={"1px solid #EBEAEA"}
//         borderRadius={5}
//         h={"10"}
//         alignItems={"center"}
//         onClick={showOpenFileDlg}
//         overflow={"hidden"}
//         borderColor={errorBorder && "#f00"}
//       >
//         <Text
//           bg={"#EBEAEA"}
//           h={10}
//           m={0}
//           paddingInline={8}
//           textAlign={"center"}
//           alignItems={"center"}
//           pt={2}
//           fontSize={"md"}
//           fontWeight={"medium"}
//         >
//           Browse
//         </Text>
//         <Text>{filename || "Choose file"}</Text>
//         <Input
//           ref={inputOpenFileRef}
//           type="file"
//           onChange={onChange}
//           display={"none"}
//           accept={doctype || "image/png, image/jpg, image/jpeg ,.pdf"}
//         />
//       </HStack>
//       {error && (
//         <Text fontSize={"sm"} color={"#f00"}>
//           {error}
//         </Text>
//       )}
//     </FormControl>
//   );
// };

// export default SelectImage;
